/**
 * O detalhe de tarefa comandado pela URL, para as tres telas que o abrem.
 *
 * Nasceu dentro de `screens/Tasks.tsx` na §8m e saiu de la quando a matriz e o
 * calendario precisaram do mesmo comportamento. Tres copias divergiriam na
 * primeira borda que alguem mexesse — e as bordas aqui sao justamente as que
 * ninguem lembra de replicar: a tarefa na lixeira, a resposta que ainda nao
 * chegou, o `key` que remonta o formulario ao trocar de card.
 *
 * A regra que as tres telas passam a compartilhar: **abrir empilha, fechar
 * substitui**. Sem o replace, fechar pelo ✕ deixaria para tras um passo de
 * historico que o "voltar" do aparelho usaria para reabrir o painel que a
 * pessoa acabou de fechar.
 */
import { useNavigate, useParams } from 'react-router';
import type { Task } from '@cleartask/shared';
import { CLASSE_PAINEL_FIXO, TaskDetail } from './TaskDetail.js';
import { useRestoreTask, useTask } from './useTasks.js';

/**
 * O que um endereco de tarefa pode encontrar.
 *
 * Enquanto o detalhe era estado local so havia dois casos (aberto e fechado),
 * porque a tarefa vinha do card que a pessoa acabou de clicar. Um endereco
 * chega de qualquer lugar: de um link velho, de uma tarefa que foi para a
 * lixeira desde entao, ou antes de a resposta voltar.
 */
export type EstadoDoDetalhe =
  | { tipo: 'fechado' }
  | { tipo: 'procurando' }
  | { tipo: 'aberto'; task: Task }
  | { tipo: 'na-lixeira'; task: Task }
  | { tipo: 'nao-encontrada' };

export function estadoDoDetalhe(
  id: string | undefined,
  task: Task | null,
  procurando: boolean,
): EstadoDoDetalhe {
  if (id === undefined) return { tipo: 'fechado' };

  if (task !== null) {
    // A rota devolve a tarefa mesmo na lixeira, de proposito: 404 num link de
    // tarefa excluida mentiria sobre o motivo. O editor e que nao serve — ela
    // nao esta em lugar nenhum para ser editada.
    return task.deletedAt === null
      ? { tipo: 'aberto', task }
      : { tipo: 'na-lixeira', task };
  }

  // A ordem importa: afirmar ausencia antes de haver resposta faria o link
  // piscar um erro que nao aconteceu.
  if (procurando) return { tipo: 'procurando' };

  // Consulta terminada e sem tarefa: 404 do servidor, id que nao e uuid, ou
  // resposta vazia. Para quem colou o link, os tres dizem a mesma coisa.
  return { tipo: 'nao-encontrada' };
}

/**
 * Liga o detalhe ao endereco da tela que chamou.
 *
 * `base` e o caminho da tela (`/tarefas`, `/matriz`, `/calendario`) e `sufixo`
 * carrega os parametros de consulta que precisam sobreviver ao ir e vir —
 * filtro de lista, de tag, visao do calendario. Abrir uma tarefa a partir de
 * "#casa" e voltar dali para a lista inteira seria trocar a tela por baixo de
 * quem so abriu um item.
 */
export function useDetalheNaUrl(base: string, sufixo = '') {
  const { id } = useParams();
  const navigate = useNavigate();
  const { task, procurando } = useTask(id);

  const abrirPorId = (alvoId: string) => navigate(`${base}/${alvoId}${sufixo}`);

  return {
    detalhe: estadoDoDetalhe(id, task, procurando),
    abrir: (alvo: Task) => abrirPorId(alvo.id),
    // Por id, e nao por `Task`: quem navega para a tarefa-pai a partir de uma
    // subtarefa tem em maos o `parentId`, e nao a linha inteira.
    abrirPorId,
    fechar: () => navigate(`${base}${sufixo}`, { replace: true }),
  };
}

/**
 * O painel, nos quatro desfechos.
 *
 * Uma unica peca para os quatro em vez de espalhar `detalhe.tipo === ...` por
 * tres telas: e a forma de garantir que a matriz nao ganhe, por esquecimento, o
 * comportamento silencioso que a aba Tarefas ja abandonou.
 */
export function DetalheNaUrl({
  detalhe,
  fechar,
  abrirPorId,
  fixo = false,
}: {
  detalhe: EstadoDoDetalhe;
  fechar: () => void;
  /** Navega para outra tarefa sem fechar o painel — hoje so a tarefa-pai. */
  abrirPorId?: (id: string) => void;
  /**
   * Painel encaixado ao lado da lista (aba Tarefas em tela larga). So a aba
   * Tarefas o usa: na matriz e no calendario a grade precisa da largura toda.
   */
  fixo?: boolean;
}) {
  if (detalhe.tipo === 'fechado') {
    // No painel fixo a coluna existe sempre — some-la e devolve-la a cada
    // clique faria a lista mudar de largura e pular sob o cursor. Vazia, ela
    // diz como se preenche (spec 9.5).
    return fixo ? (
      <aside aria-label="Detalhe" className={`${CLASSE_PAINEL_FIXO} grid place-items-center`}>
        <p className="text-sm text-text-mut">Selecione uma tarefa</p>
      </aside>
    ) : null;
  }

  /*
    `key` no id: o painel guarda o formulario em estado proprio, inicializado a
    partir da tarefa. Sem remontar, trocar de card com o detalhe aberto deixaria
    os campos mostrando a tarefa ANTERIOR — e salvar gravaria o texto de uma
    tarefa por cima de outra.
  */
  if (detalhe.tipo === 'aberto') {
    return (
      <TaskDetail
        key={detalhe.task.id}
        task={detalhe.task}
        onClose={fechar}
        fixo={fixo}
        {...(abrirPorId ? { onAbrirTarefa: abrirPorId } : {})}
      />
    );
  }

  return <LinkSemDetalhe detalhe={detalhe} fechar={fechar} fixo={fixo} />;
}

/**
 * Os casos em que o endereco nao abre um editor.
 *
 * Existe em vez de simplesmente nao renderizar nada: um link que abre a lista
 * sem dizer palavra parece o app ignorando o clique. Aqui a tela diz o que
 * aconteceu e qual e o proximo passo — a mesma regra dos estados vazios
 * (spec 9.5).
 */
function LinkSemDetalhe({
  detalhe,
  fechar,
  fixo,
}: {
  detalhe: Extract<EstadoDoDetalhe, { tipo: 'procurando' | 'na-lixeira' | 'nao-encontrada' }>;
  fechar: () => void;
  fixo: boolean;
}) {
  const restaurar = useRestoreTask();

  const copy = {
    procurando: { titulo: 'Abrindo a tarefa…', passo: null },
    'na-lixeira': {
      titulo: 'Esta tarefa está na lixeira.',
      passo: 'Restaure para voltar a editá-la. Ela sai de vez 30 dias após a exclusão.',
    },
    'nao-encontrada': {
      titulo: 'Tarefa não encontrada.',
      passo: 'O link pode estar errado, ou a tarefa já foi removida de vez.',
    },
  }[detalhe.tipo];

  return (
    <aside
      // Mesma regra do `TaskDetail`: encaixado, nao e dialogo.
      role={fixo ? undefined : 'dialog'}
      aria-label="Detalhe"
      className={
        fixo
          ? `${CLASSE_PAINEL_FIXO} pb-4`
          : 'fixed inset-x-0 bottom-0 z-[25] rounded-t border-t border-border bg-surface p-4 shadow-card md:inset-y-0 md:left-auto md:right-0 md:w-detail md:rounded-none md:border-l md:border-t-0'
      }
      onKeyDown={(event) => {
        if (event.key === 'Escape') fechar();
      }}
    >
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-md font-semibold">Detalhe</h2>
        <button
          type="button"
          onClick={fechar}
          aria-label="Fechar detalhe"
          className="min-h-touch rounded-sm px-2 text-text-mut hover:bg-surface-2"
        >
          ✕
        </button>
      </div>

      <p className="font-medium">{copy.titulo}</p>
      {copy.passo !== null ? <p className="mt-1 text-sm text-text-mut">{copy.passo}</p> : null}

      {/*
        Restaurar AQUI, e nao um recado mandando abrir Mais → Lixeira.

        A §8m adiou este botao para nao duplicar a mutacao que vivia dentro de
        `TrashList`; a mutacao virou `useRestoreTask` e a razao caiu junto.
        Quem chegou por um link quer aquela tarefa — mandar procurar a mesma
        tarefa noutra tela e trabalho que a interface pode fazer.

        Nao ha confirmacao: restaurar e a acao reversivel (excluir de novo
        devolve ao estado anterior), e a convencao 6 reserva o dialogo para o
        desfazer comprovadamente incompleto.
      */}
      {detalhe.tipo === 'na-lixeira' ? (
        <button
          type="button"
          onClick={() => restaurar.mutate(detalhe.task)}
          disabled={restaurar.isPending}
          className="min-h-touch mt-4 w-full rounded bg-brand-blue px-3 font-medium text-white disabled:opacity-60"
        >
          Restaurar
        </button>
      ) : null}
    </aside>
  );
}
